import { useState } from 'react';
import { PlusIcon, type MyIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { VEHICLE_NAME_MAX_LENGTH } from '@repo/domain';
import { useVehicleActions } from '@repo/domain-service';
import { Button, Input, Modal } from '@repo/ui';
import { VehicleRegistrationInput } from '@repo/entities';

import { PHONE_LAYOUT_CONTAINER_ID } from '@shared/constants/layout';
import { handleRepositoryError } from '@shared/utils/handleRepositoryError';

interface Props {
  testId: TestId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVehicleAdded: () => void;
}

const AddVehicleIcon: MyIcon = (props) => <PlusIcon {...props} className="h-3 w-3" />;

export default function VehicleCreationModal({ testId, open, onOpenChange, onVehicleAdded }: Props) {
  const [registrationNumber, setRegistrationNumber] = useState<string>('');
  const [vehicleName, setVehicleName] = useState<string>('');

  const { createVehicle, savingVehicles } = useVehicleActions();

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) resetForm();
  };

  const handleClose = () => {
    handleOpenChange(false);
  };

  const resetForm = () => {
    setRegistrationNumber('');
    setVehicleName('');
  };

  const handleCreateNewVehicle = async () => {
    try {
      await createVehicle([
        {
          name: vehicleName,
          registrationNumber,
        },
      ]);
      handleClose();
      onVehicleAdded();
    } catch (error) {
      handleRepositoryError(error, {
        fallback: tr('reservations:vehicle_creation.error'),
        testId,
        businessCodes: {
          vehicleAlreadyExists: tr('reservations:vehicle_creation.already_exists'),
        },
      });
    }
  };

  const footer = (
    <Button
      testId={testId.append('addVehicle')}
      color="brand"
      size="lg"
      label={tr('reservations:vehicle_creation.add_vehicle')}
      onClick={handleCreateNewVehicle}
      disabled={!registrationNumber.trim() || savingVehicles}
      rightIcon={AddVehicleIcon}
      className="w-full"
    />
  );

  const portalTarget = document.getElementById(PHONE_LAYOUT_CONTAINER_ID) ?? undefined;

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      portalTo={portalTarget}
      testId={testId.append('drawer')}
      title={tr('reservations:vehicle_creation.title')}
      footer={footer}
    >
      <div className="flex flex-col gap-4 pt-4 pb-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center justify-start gap-0.5">
              <div className="text-heading text-sm leading-5 font-medium">
                {tr('reservations:vehicle_creation.registration_number')}
              </div>
              <div className="text-fg-danger text-sm leading-5 font-medium">*</div>
            </div>
            <VehicleRegistrationInput
              testId={testId.append('registrationNumber')}
              value={registrationNumber}
              onChange={setRegistrationNumber}
            />
          </div>

          {/* todo toggle save for later*/}
          {/* <div className="bg-primary-soft border-brand flex flex-col gap-2.5 rounded-xl border p-4 shadow-sm">
            <div className="inline-flex items-center justify-start gap-2">
              <div className="relative h-6 w-6 overflow-hidden">
                <CarIcon size={24} className="text-brand-strong" />
              </div>
              <div className="text-heading flex-1 text-sm leading-4 font-medium">
                {tr('reservations:vehicle_creation.save_for_later')}
              </div>
              <Switch testId={testId.append('saveForLaterToggle')} checked={saveForLater} onChange={setSaveForLater} />
            </div>
          </div> */}

          <div className="flex flex-col gap-1">
            <div className="inline-flex items-center justify-between">
              <div className="text-heading text-sm leading-5 font-medium">
                {tr('reservations:vehicle_creation.vehicle_name')}
              </div>
              <div className="text-body-subtle text-xs leading-4 font-normal">
                {tr('reservations:vehicle_creation.vehicle_name_optional')}
              </div>
            </div>
            <Input
              testId={testId.append('vehicleName')}
              placeholder={tr('reservations:vehicle_creation.vehicle_name_placeholder')}
              value={vehicleName}
              onChange={setVehicleName}
              maxLength={VEHICLE_NAME_MAX_LENGTH}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
